<template>
  <div class="h-full p-4 relative">
    <!-- Folder Icons -->
    <div
      v-for="folder in folders"
      :key="folder.id"
      class="w-24 text-center cursor-pointer"
      :style="folder.style"
      @click="openWindow(folder)"
    >
      <div class="w-16 h-16 mx-auto flex items-center justify-center">
        <svg
          class="w-12 h-12 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
          ></path>
        </svg>
      </div>
      <p class="mt-2 text-sm text-white">{{ folder.name }}</p>
    </div>

    <!-- Folder Windows -->
    <div
      v-for="window in openWindows"
      :key="window.id"
      class="folder-window"
      :class="{ maximized: window.isMaximized }"
      :style="{
        display: window.isOpen ? 'block' : 'none',
        left: window.position.left + 'px',
        top: window.position.top + 'px',
        zIndex: window.zIndex || 1,
        width: window.isMaximized ? '100%' : '90%',
        height: window.isMaximized ? '100%' : '60%',
      }"
      @mousedown="bringToFront('folder', window.id)"
    >
      <div
        class="window-header"
        @mousedown="startDrag($event, window)"
        @touchstart="startDrag($event, window)"
      >
        <span class="window-title">{{ window.name }}</span>
        <div class="window-controls">
          <button
            class="close"
            @click.stop="handleClose($event, window)"
            @touchend.prevent.stop="handleClose($event, window)"
          ></button>
          <button
            class="minimize"
            @click="handleMinimize($event, window)"
            @touchstart.prevent="handleMinimize($event, window)"
          ></button>
          <button
            class="maximize"
            @click="handleMaximize($event, window)"
            @touchstart.prevent="handleMaximize($event, window)"
          ></button>
        </div>
      </div>
      <div class="window-content">
        <div class="space-y-2">
          <div
            v-for="item in window.content"
            :key="item.slug"
            class="flex items-center space-x-2 cursor-pointer"
            @click="handleOpenDocument(item)"
          >
            <svg
              class="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              ></path>
            </svg>
            <span>{{ item.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Document Viewers -->
    <DocumentViewer
      v-for="doc in openDocuments"
      :key="doc.slug"
      :slug="doc.slug"
      :initial-position="doc.position"
      :z-index="doc.zIndex || 1"
      :is-open="doc.isOpen"
      @close="closeDocument(doc.slug)"
      @minimize="minimizeDocument(doc.slug)"
      @focus="bringToFront('document', doc.slug)"
    />

    <!-- Applications Menu -->
    <div v-if="isApplicationsMenuVisible" class="applications-menu">
      <a href="https://www.cheddarlist.com" target="_blank">CheddarList</a>
      <a href="https://www.check.cv" target="_blank">check.cv</a>
    </div>
  </div>
</template>

<script>
import DocumentViewer from "./DocumentViewer.vue";

export default {
  name: "AppDesktop",
  components: {
    DocumentViewer,
  },
  data() {
    return {
      folders: [
        {
          id: 1,
          name: "Documents",
          type: "folder",
          style: { position: "absolute", top: "20px", left: "20px" },
          content: [
            { name: "about_us.md", slug: "about-us" },
            { name: "our_ethos.md", slug: "our-ethos" },
            { name: "contacts.md", slug: "contacts" },
          ],
        },
        {
          id: 2,
          name: "Terms & Policies",
          type: "folder",
          style: { position: "absolute", top: "20px", left: "140px" },
          content: [
            { name: "terms_of_service.md", slug: "terms-of-service" },
            { name: "privacy_policy.md", slug: "privacy-policy" },
            { name: "copyright.md", slug: "copyright" },
          ],
        },
        {
          id: 3,
          name: "Creative",
          type: "folder",
          style: { position: "absolute", top: "120px", left: "20px" },
          content: [
            { name: "icon.png", slug: "icon" },
            { name: "bbt_logo.png", slug: "bbt-logo" },
          ],
        },
      ],
      openWindows: [],
      activeDocument: null,
      isDragging: false,
      dragOffset: { x: 0, y: 0 },
      openDocuments: [],
      currentZIndex: 1,
      isMobile: false,
      isApplicationsMenuVisible: false,
    };
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  watch: {
    openWindows: {
      handler(newWindows) {
        // Emit window updates whenever openWindows changes
        this.$emit(
          "window-update",
          newWindows.map((window) => ({
            id: window.id,
            name: window.name,
            isOpen: window.isOpen,
          }))
        );
      },
      deep: true,
    },
    openDocuments: {
      handler(newDocs) {
        this.$emit(
          "document-update",
          newDocs.map((doc) => ({
            slug: doc.slug,
            name: doc.slug
              .split("-")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            isOpen: doc.isOpen,
          }))
        );
      },
      deep: true,
    },
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 963;
      this.adjustWindowPositions();
    },
    adjustWindowPositions() {
      this.openWindows.forEach((window) => {
        if (this.isMobile) {
          window.position = this.getResponsivePosition();
        } else {
          window.position = this.keepInBounds(window.position);
        }
      });

      this.openDocuments = this.openDocuments.map((doc) => ({
        ...doc,
        position: this.isMobile
          ? this.getResponsivePosition()
          : this.keepInBounds(doc.position),
      }));
    },
    keepInBounds(position) {
      let maxWidth;
      if (window.innerWidth >= 964) {
        maxWidth = Math.min(window.innerWidth - 400, 963);
      } else if (window.innerWidth > 768) {
        maxWidth = window.innerWidth - 200;
      } else {
        maxWidth = window.innerWidth * 0.95;
      }

      const maxHeight =
        window.innerHeight - (window.innerWidth <= 768 ? 100 : 300);

      return {
        left: Math.max(0, Math.min(position.left, maxWidth)),
        top: Math.max(0, Math.min(position.top, maxHeight)),
      };
    },
    getResponsivePosition() {
      return this.isMobile
        ? {
            left: 0,
            top: window.innerHeight * 0.1,
          }
        : {
            left: 100,
            top: 100,
          };
    },
    bringToFront(type, id) {
      this.currentZIndex++;
      if (type === "folder") {
        const window = this.openWindows.find((w) => w.id === id);
        if (window) {
          window.zIndex = this.currentZIndex;
        }
      } else if (type === "document") {
        const doc = this.openDocuments.find((d) => d.slug === id);
        if (doc) {
          doc.zIndex = this.currentZIndex;
        }
      }
    },
    openWindow(folder) {
      if (!this.openWindows.find((w) => w.id === folder.id)) {
        this.currentZIndex++;
        this.openWindows.push({
          ...folder,
          isOpen: true,
          isMaximized: false,
          position: this.getResponsivePosition(),
          zIndex: this.currentZIndex,
        });
      } else {
        const existingWindow = this.openWindows.find((w) => w.id === folder.id);
        existingWindow.isOpen = true;
        this.bringToFront("folder", folder.id);
      }
    },
    closeWindow(window) {
      this.openWindows = this.openWindows.filter((w) => w.id !== window.id);
    },
    minimizeWindow(window) {
      window.isOpen = false;
    },
    maximizeWindow(window) {
      window.isMaximized = !window.isMaximized;
      if (window.isMaximized) {
        window.position = { left: 0, top: 0 };
        window.width = "100%";
        window.height = "100%";
      } else {
        window.position = { left: 100, top: 100 };
        window.width = "90%";
        window.height = "60%";
      }
    },
    handleOpenDocument(item) {
      const existingDoc = this.openDocuments.find(
        (doc) => doc.slug === item.slug
      );
      if (!existingDoc) {
        this.currentZIndex++;
        const offset = this.isMobile ? 0 : this.openDocuments.length * 20;
        const position = this.keepInBounds({
          left: 120 + offset,
          top: 120 + offset,
        });

        this.openDocuments.push({
          ...item,
          position,
          zIndex: this.currentZIndex,
          isOpen: true,
        });

        if (window.history.pushState) {
          window.history.pushState(null, '', `/${item.slug}`);
        }
      } else {
        existingDoc.isOpen = true;
        this.bringToFront("document", item.slug);
      }
    },
    closeDocument(slug) {
      this.openDocuments = this.openDocuments.filter(
        (doc) => doc.slug !== slug
      );
      if (window.location.pathname === `/${slug}`) {
        window.history.pushState(null, '', '/');
      }
    },
    startDrag(event, window) {
      if (window.isMaximized) return;

      this.isDragging = true;
      if (event.type === "touchstart") {
        const touch = event.touches[0];
        this.dragOffset = {
          x: touch.clientX - window.position.left,
          y: touch.clientY - window.position.top,
        };

        const touchMoveHandler = (e) => {
          e.preventDefault(); // Prevent scrolling
          if (this.isDragging) {
            const touch = e.touches[0];
            const newPosition = this.keepInBounds({
              left: touch.clientX - this.dragOffset.x,
              top: touch.clientY - this.dragOffset.y,
            });
            window.position = newPosition;
          }
        };

        const touchEndHandler = () => {
          this.isDragging = false;
          document.removeEventListener("touchmove", touchMoveHandler);
          document.removeEventListener("touchend", touchEndHandler);
        };

        document.addEventListener("touchmove", touchMoveHandler, {
          passive: false,
        });
        document.addEventListener("touchend", touchEndHandler);
      } else {
        this.dragOffset = {
          x: event.clientX - window.position.left,
          y: event.clientY - window.position.top,
        };

        const moveHandler = (e) => {
          if (this.isDragging) {
            window.position = {
              left: e.clientX - this.dragOffset.x,
              top: e.clientY - this.dragOffset.y,
            };
          }
        };

        const upHandler = () => {
          this.isDragging = false;
          document.removeEventListener("mousemove", moveHandler);
          document.removeEventListener("mouseup", upHandler);
        };

        document.addEventListener("mousemove", moveHandler);
        document.addEventListener("mouseup", upHandler);
      }
    },
    handleClose(event, window) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      this.isDragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      this.closeWindow(window);
    },
    handleMinimize(event, window) {
      event.stopPropagation();
      this.minimizeWindow(window);
    },
    handleMaximize(event, window) {
      event.stopPropagation();
      this.maximizeWindow(window);
    },
    toggleWindow(window) {
      const existingWindow = this.openWindows.find((w) => w.id === window.id);
      if (existingWindow) {
        existingWindow.isOpen = !existingWindow.isOpen;
        if (existingWindow.isOpen) {
          this.bringToFront("folder", window.id);
        }
      }
    },
    toggleDocument(doc) {
      const existingDoc = this.openDocuments.find((d) => d.slug === doc.slug);
      if (existingDoc) {
        existingDoc.isOpen = !existingDoc.isOpen;
        if (existingDoc.isOpen) {
          this.bringToFront("document", doc.slug);
        }
      } else {
        const item = this.findDocumentInFolders(doc.slug);
        if (item) {
          this.handleOpenDocument(item);
        }
      }
    },
    findDocumentInFolders(slug) {
      for (const folder of this.folders) {
        const item = folder.content.find((item) => item.slug === slug);
        if (item) return item;
      }
      return null;
    },
    minimizeDocument(slug) {
      const doc = this.openDocuments.find((d) => d.slug === slug);
      if (doc) {
        doc.isOpen = false;
      }
    },
    toggleApplicationsMenu() {
      this.isApplicationsMenuVisible = !this.isApplicationsMenuVisible;
    },
    openDocumentBySlug(slug) {
      for (const folder of this.folders) {
        const doc = folder.content.find(item => item.slug === slug);
        if (doc) {
          this.handleOpenDocument(doc);
          break;
        }
      }
    },
  },
};
</script>

<style scoped>
.folder-window {
  display: none;
  position: absolute;
  width: 90%;
  max-width: 400px;
  height: 60%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  resize: both;
  background: #1a1a1a;
  color: #ffffff;
}

@media (max-width: 936px) {
  .folder-window {
    width: 90%;
    max-width: 90%;
    height: 40%;
    max-height: 40vh;
    left: 5% !important;
    top: 5% !important;
    resize: none;
    position: fixed;
  }

  .folder-window.maximized {
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    left: 0 !important;
    top: 0 !important;
  }

  .window-controls {
    gap: 4px;
  }

  .window-controls button {
    width: 14px;
    height: 14px;
    min-width: 14px;
    min-height: 14px;
    margin: 0 2px;
  }
}

.window-header {
  background: #2d2d2d;
  padding: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: move;
}

.window-title {
  font-size: 14px;
  font-weight: bold;
}

.window-controls {
  display: flex;
  gap: 8px;
}

.window-controls button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
}

.window-controls .close {
  background: #f56565;
}

.window-controls .minimize {
  background: #f6e05e;
}

.window-controls .maximize {
  background: #48bb78;
}

.window-content {
  padding: 16px;
  overflow-y: auto;
}

.applications-menu {
  position: absolute;
  bottom: 60px;
  left: 10px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1000;
  width: 200px;
  color: #000000;
}

.applications-menu a {
  display: block;
  padding: 8px;
  color: #000000;
  text-decoration: none;
}

.applications-menu a:hover {
  background: #f0f0f0;
  border-radius: 4px;
}

@media (max-width: 936px) {
  .applications-menu {
    width: 180px;
  }
}
</style>
